import { FC, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import styled from 'styled-components'
import { Icon, IconColor, IconTypes } from '@app/ui'
import type { TawktoPosition } from '@app/hooks/useTawkto'
import { computeLauncherPlacement } from './positionTawkWidget'
import { openTawkSupportChat, isTawkLoaded } from './supportChatApi'
import { SupportChatSessionOverlay } from './SupportChatSessionOverlay'

interface SupportChatControlsProps {
  position: TawktoPosition
}

export const SupportChatControls: FC<SupportChatControlsProps> = ({ position }) => {
  const [ launcher, setLauncher ] = useState(() => computeLauncherPlacement(position))
  const [ chatOpen, setChatOpen ] = useState(false)
  const [ tawkReady, setTawkReady ] = useState(() => isTawkLoaded())

  useLayoutEffect(() => {
    const updateLayout = () => setLauncher(computeLauncherPlacement(position))
    const onOpened = () => setChatOpen(true)
    const onClosed = () => setChatOpen(false)
    const onReady  = () => setTawkReady(true)
    updateLayout()
    window.addEventListener('tipsterizer-tawk-chat-opened', onOpened)
    window.addEventListener('tipsterizer-tawk-chat-closed', onClosed)
    window.addEventListener('tipsterizer-tawk-ready',       onReady)
    const mo = new MutationObserver(updateLayout)
    mo.observe(document.body, { childList: true, subtree: true, attributes: true })
    window.addEventListener('resize', updateLayout, { passive: true })
    window.addEventListener('scroll', updateLayout, { passive: true })
    return () => {
      mo.disconnect()
      window.removeEventListener('tipsterizer-tawk-chat-opened', onOpened)
      window.removeEventListener('tipsterizer-tawk-chat-closed', onClosed)
      window.removeEventListener('tipsterizer-tawk-ready',       onReady)
      window.removeEventListener('resize', updateLayout)
      window.removeEventListener('scroll', updateLayout)
    }
  }, [ position ])

  useEffect(() => {
    setChatOpen(document.body.hasAttribute('data-tawk-chat-open'))
  }, [])

  const openChat = useCallback(() => {
    openTawkSupportChat()
    setChatOpen(true)
  }, [])

  return (
    <>
      <SupportChatSessionOverlay />
      {!chatOpen && tawkReady && (
        <LauncherButton
          type="button"
          aria-label="Open support chat"
          $bottom={launcher.bottomPx}
          $centerX={launcher.centerX}
          onClick={openChat}
        >
          <Icon type={IconTypes.supportIcon} color={IconColor.white} width={28} height={28} />
        </LauncherButton>
      )}
    </>
  )
}

const LauncherButton = styled.button<{ $bottom: number; $centerX: number }>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $centerX }) => $centerX}px;
  transform: translateX(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 998;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-quinary, #6c47ff);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);

  &:hover {
    transform: translateX(-50%) scale(1.04);
  }

  &:active {
    transform: translateX(-50%) scale(0.96);
  }
`
